import {
  AfterViewInit,
  Component,
  ElementRef,
  forwardRef,
  Input,
  ViewChild,
} from '@angular/core';

import {
  ControlValueAccessor,
  NG_VALUE_ACCESSOR,
  ReactiveFormsModule,
} from '@angular/forms';

import { CommonModule } from '@angular/common';
import { GoogleRecaptchaLoaderService } from '../../service/google-recaptcha-loader.service';

declare const grecaptcha: {
  ready(callback: () => void): void;
  render(
    container: HTMLElement,
    parameters: {
      sitekey: string;
      theme: 'light' | 'dark';
      size: 'normal' | 'compact';
      tabindex: number;
      callback: (token: string) => void;
      'expired-callback': () => void;
      'error-callback': () => void;
    }
  ): number;
  reset(widgetId?: number): void;
  remove(widgetId: number): void;
};

type OnChangeFn = (value: string) => void;
type OnTouchedFn = () => void;

@Component({
  selector: 'app-google-recaptcha',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule],
  templateUrl: './google-recaptcha.component.html',
  styleUrls: ['./google-recaptcha.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => GoogleRecaptchaComponent),
      multi: true,
    },
  ],
})
export class GoogleRecaptchaComponent
  implements AfterViewInit, ControlValueAccessor
{
  @Input() siteKey!: string;

  @Input() theme: 'light' | 'dark' = 'light';

  @Input() size: 'normal' | 'compact' = 'normal';

  @Input() tabindex = 0;

  @ViewChild('captchaContainer', { static: true })
  captchaContainer!: ElementRef<HTMLDivElement>;

  private widgetId: number | undefined;

  private value = '';

  private onChange: OnChangeFn = () => undefined;

  private onTouched: OnTouchedFn = () => undefined;

  disabled = false;

  constructor(private readonly loader: GoogleRecaptchaLoaderService) {}

  ngAfterViewInit(): void {
    void this.initRecaptcha();
  }

  private async initRecaptcha(): Promise<void> {
    await this.loader.load();

    grecaptcha.ready(() => {
      this.renderCaptcha();
    });
  }

  private renderCaptcha(): void {
    if (!this.siteKey) {
      console.error('reCAPTCHA Site Key is missing');
      return;
    }

    this.widgetId = grecaptcha.render(this.captchaContainer.nativeElement, {
      sitekey: this.siteKey,
      theme: this.theme,
      size: this.size,
      tabindex: this.tabindex,

      callback: (token: string) => {
        this.value = token;
        this.onChange(token);
        this.onTouched();
      },

      'expired-callback': () => {
        this.reset();
      },

      'error-callback': () => {
        this.reset();
      },
    });
  }

  writeValue(value: string | null): void {
    this.value = value ?? '';

    if (!this.value && this.widgetId !== undefined) {
      grecaptcha.reset(this.widgetId);
    }
  }

  registerOnChange(fn: OnChangeFn): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: OnTouchedFn): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  reset(): void {
    this.value = '';

    if (this.widgetId !== undefined) {
      grecaptcha.reset(this.widgetId);
    }

    this.onChange('');
    this.onTouched();
  }
}
